import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, ExternalLink, Facebook, Image as ImageIcon } from 'lucide-react';
import { COMPANY } from '@/lib/data';
import { publicApi } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { fadeUp, stagger } from '@/lib/motion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const TikTokIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
        <path d="M14.5 3h2.6c.2 1.7 1.1 3.1 2.6 4 .9.5 1.9.8 3 .8v2.7c-1.9 0-3.7-.6-5.2-1.7v6.8c0 3.6-2.9 6.6-6.6 6.6S4.3 19.2 4.3 15.6c0-3.6 2.9-6.6 6.6-6.6.4 0 .8 0 1.2.1V12c-.4-.1-.8-.2-1.2-.2-2.1 0-3.8 1.7-3.8 3.8s1.7 3.8 3.8 3.8 3.8-1.7 3.8-3.8V3z"/>
    </svg>
);

const PLATFORM_META = {
    instagram: { Icon: Instagram, label: 'Instagram', badge: 'bg-[hsl(var(--kche-pink))] text-white', url: COMPANY.instagramUrl },
    tiktok: { Icon: TikTokIcon, label: 'TikTok', badge: 'bg-[hsl(var(--kche-dark))] text-white', url: COMPANY.tiktokUrl },
    facebook: { Icon: Facebook, label: 'Facebook', badge: 'bg-[hsl(var(--kche-blue))] text-white', url: COMPANY.facebookUrl },
};

const PLATFORM_FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'facebook', label: 'Facebook' },
];

const loadScriptOnce = (src, id) => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.src = src;
    s.id = id;
    s.async = true;
    document.body.appendChild(s);
};

const reprocessEmbeds = () => {
    try {
        if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();
    } catch {}
    // For TikTok, re-injecting the script forces re-processing of blockquotes.
    try {
        const prev = document.getElementById('tt-embed-script');
        if (prev) prev.remove();
        const s = document.createElement('script');
        s.src = 'https://www.tiktok.com/embed.js';
        s.id = 'tt-embed-script';
        s.async = true;
        document.body.appendChild(s);
    } catch {}
};

const SocialCard = ({ post }) => {
    const meta = PLATFORM_META[post.platform] || PLATFORM_META.instagram;
    const Icon = meta.Icon;
    const cardRef = useRef(null);
    const [embedFailed, setEmbedFailed] = useState(false);
    const fallbackUrl = post.post_url || post.embed_url || meta.url;

    useEffect(() => {
        // Re-process IG embeds shortly after mount/update
        if (!post.embed_code) return;
        const t = setTimeout(() => {
            try { if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process(); } catch {}
        }, 250);
        return () => clearTimeout(t);
    }, [post.embed_code]);

    return (
        <motion.article
            ref={cardRef}
            variants={fadeUp}
            data-testid="social-card"
            className="rounded-3xl bg-white border border-border overflow-hidden flex flex-col shadow-soft hover:shadow-pop transition-all"
        >
            <div className="flex items-center gap-2 px-5 pt-5">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold font-display ${meta.badge} shadow-soft`}>
                    <Icon className="size-3.5" /> {meta.label}
                </span>
                {post.handle ? <span className="text-xs text-muted-foreground truncate">{post.handle}</span> : null}
            </div>
            {post.title ? (
                <h4 className="px-5 mt-3 font-display font-bold text-foreground text-lg">{post.title}</h4>
            ) : null}
            {post.description ? (
                <p className="px-5 mt-1 text-sm text-muted-foreground">{post.description}</p>
            ) : null}
            <div className="mt-4 px-5">
                {post.embed_code && !embedFailed ? (
                    <div
                        data-testid="social-card-embed"
                        className="social-embed-wrap relative w-full overflow-hidden rounded-2xl border border-border bg-secondary"
                        // dangerouslySetInnerHTML is required for platform embeds (Instagram blockquote, TikTok blockquote, Facebook iframe).
                        dangerouslySetInnerHTML={{ __html: post.embed_code }}
                    />
                ) : post.image_url ? (
                    <a
                        href={fallbackUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="social-card-thumb"
                        className="block relative aspect-square rounded-2xl overflow-hidden border border-border bg-secondary group"
                    >
                        <img
                            src={post.image_url}
                            alt={post.title || meta.label}
                            loading="lazy"
                            onError={() => setEmbedFailed(true)}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                ) : (
                    <div data-testid="social-card-empty" className="flex items-center justify-center aspect-square rounded-2xl border border-dashed border-border bg-secondary text-muted-foreground">
                        <div className="text-center px-4">
                            <ImageIcon className="size-7 mx-auto opacity-60" />
                            <p className="mt-2 text-xs">Vista previa no disponible. Toca el botón para ver la publicación.</p>
                        </div>
                    </div>
                )}
            </div>
            <div className="px-5 pb-5 pt-4 mt-auto">
                <a
                    href={fallbackUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="social-card-original-link"
                    onClick={() => trackEvent('social_click', window.location.pathname, { platform: post.platform, from: 'card_button' })}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-fun text-white font-bold font-display px-4 py-2 text-sm shadow-pop hover:scale-[1.02] transition"
                >
                    Ver publicación original <ExternalLink className="size-4" />
                </a>
            </div>
        </motion.article>
    );
};

export const SocialWall = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');

    useEffect(() => { publicApi.getSocial().then(setItems).catch(() => {}); }, []);

    // Load embed scripts once on mount.
    useEffect(() => {
        loadScriptOnce('https://www.instagram.com/embed.js', 'ig-embed-script');
        loadScriptOnce('https://www.tiktok.com/embed.js', 'tt-embed-script');
    }, []);

    // Re-process whenever the visible items change (filter switches, new fetch).
    const visible = useMemo(
        () => (filter === 'all' ? items : items.filter((s) => s.platform === filter)),
        [items, filter]
    );

    useEffect(() => {
        const t = setTimeout(() => reprocessEmbeds(), 350);
        return () => clearTimeout(t);
    }, [visible]);

    const onClick = (p) => trackEvent('social_click', window.location.pathname, { platform: p, from: 'wall' });

    return (
        <section data-testid="home-social-wall" className="relative bg-kche-soft py-16 sm:py-20 lg:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <p className="text-sm font-bold font-display text-[hsl(var(--kche-pink))] uppercase tracking-wider">Síguenos</p>
                        <h2 className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">@kchediversiones</h2>
                        <p className="mt-2 text-muted-foreground max-w-prose text-base sm:text-lg">Conoce lo que vivimos cada día y los próximos eventos.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <a href={COMPANY.instagramUrl} target="_blank" rel="noreferrer" onClick={() => onClick('instagram')} data-testid="social-wall-instagram" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-pink))] text-white font-bold font-display px-4 py-2 text-sm shadow-pop hover:brightness-110"><Instagram className="size-4" /> Instagram</a>
                        <a href={COMPANY.tiktokUrl} target="_blank" rel="noreferrer" onClick={() => onClick('tiktok')} data-testid="social-wall-tiktok" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-dark))] text-white font-bold font-display px-4 py-2 text-sm shadow-pop hover:brightness-110"><TikTokIcon className="size-4" /> TikTok</a>
                        <a href={COMPANY.facebookUrl} target="_blank" rel="noreferrer" onClick={() => onClick('facebook')} data-testid="social-wall-facebook" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-blue))] text-white font-bold font-display px-4 py-2 text-sm shadow-pop hover:brightness-110"><Facebook className="size-4" /> Facebook</a>
                    </div>
                </div>

                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList data-testid="social-wall-filter-tabs" className="flex flex-wrap gap-1 h-auto p-1.5 bg-white border border-border rounded-2xl shadow-soft">
                        {PLATFORM_FILTERS.map((p) => (
                            <TabsTrigger key={p.key} value={p.key} data-testid={`social-filter-${p.key}`} className="data-[state=active]:bg-gradient-fun data-[state=active]:text-white data-[state=active]:shadow-pop rounded-xl px-4 py-1.5 text-sm font-bold font-display transition">{p.label}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                <div data-testid="social-wall-grid" className="mt-8 min-h-[280px]">
                    {visible.length === 0 ? (
                        <div className="rounded-3xl bg-white border border-dashed border-border p-10 text-center">
                            <div className="mx-auto size-12 rounded-2xl bg-[hsl(var(--kche-cyan))]/15 inline-flex items-center justify-center"><ExternalLink className="size-6 text-[hsl(var(--kche-cyan))]" /></div>
                            <h3 className="mt-3 font-display font-semibold">Pronto más publicaciones</h3>
                            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">Aún no hay publicaciones en esta categoría. Mientras tanto, explóranos en nuestras redes.</p>
                        </div>
                    ) : (
                        <motion.div initial="hidden" animate="show" variants={stagger} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" key={filter}>
                            {visible.map((s) => (
                                <SocialCard key={s.id} post={s} />
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Make platform embeds responsive (override rigid widths/heights they sometimes ship with). */}
            <style>{`
                .social-embed-wrap { min-height: 320px; }
                .social-embed-wrap > * { max-width: 100% !important; margin: 0 auto !important; }
                .social-embed-wrap iframe { width: 100% !important; max-width: 100% !important; border: 0; display: block; }
                .social-embed-wrap .instagram-media,
                .social-embed-wrap .tiktok-embed { width: 100% !important; min-width: 0 !important; max-width: 100% !important; margin: 0 auto !important; }
            `}</style>
        </section>
    );
};

export default SocialWall;
